import {
  ChangeType,
  FileChangeInput,
  JobsiteFormSubmissionDocumentInput,
  JobsiteUpdateFormSubmissionInput,
} from 'apollo/generated/client-operations';
import { DeepMap } from 'react-hook-form';
import {
  CustomFormInputs,
  EditableJobsiteFormSubmissionDocument,
  EditableJobsiteFormSubmissionDocumentFile,
  EditFormData,
  JobsiteFormSubmission,
} from 'containers/jobsiteFormSubmission/types';
import { tryFileCompression } from 'utils';

const toFileChangeInput = async (
  file: EditableJobsiteFormSubmissionDocumentFile,
): Promise<FileChangeInput> => {
  if (file.changeType === ChangeType.Removed) {
    return {
      changeType: file.changeType,
      fileId: file.fileId,
    };
  }

  const fileData = await tryFileCompression(file as File);
  return {
    changeType: file.changeType,
    fileId: file.fileId,
    fileInput: { uploadData: fileData, isPublic: false },
  };
};

export const toUpdateInput = async (
  document: EditableJobsiteFormSubmissionDocument,
): Promise<JobsiteFormSubmissionDocumentInput> => {
  const { changeType, id, description, files, associationType } = document;

  return {
    changeType,
    associationType,
    id,
    description,
    files:
      document.changeType === ChangeType.Removed
        ? undefined
        : await Promise.all(
            files.filter((f) => f.changeType).map(toFileChangeInput),
          ),
  };
};

type Form = JobsiteFormSubmission['jobsiteForm']['form'];

type DocumentsSectionUpdateInput = Required<
  Pick<JobsiteUpdateFormSubmissionInput, 'documents'>
>;

export const getDocumentsUpdateInput = async (args: {
  data: CustomFormInputs<EditFormData>;
  dirtyFields: DeepMap<CustomFormInputs<EditFormData>, true>;
  defaultValues: CustomFormInputs<EditFormData>;
  form: Form;
}): Promise<DocumentsSectionUpdateInput> => {
  const { data, dirtyFields, form } = args;
  const documentFields = (form.content.edit.documentFields as string[]) ?? [];

  return {
    documents:
      documentFields &&
      (
        await Promise.all(
          documentFields.map(
            async (docField): Promise<JobsiteFormSubmissionDocumentInput[]> => {
              if (dirtyFields?.[docField]) {
                const docFieldValue = data?.[docField];
                const docFieldValueArray = Array.isArray(docFieldValue)
                  ? docFieldValue
                  : [docFieldValue];
                return Promise.all(
                  docFieldValueArray
                    ?.filter((jfsD) => jfsD.changeType)
                    .map(toUpdateInput),
                );
              }
              return [];
            },
          ),
        )
      )
        .flat()
        .filter(Boolean),
  };
};
